import { ImageMetaData } from "@/models/image-meta-data";
import { MasterConversationType } from "@/services/conversation-templates";
import { isArray } from "lodash";
import { Entity } from "./models/entity.model";
import { Intent } from "./models/intent.model";

export interface ConversationNode<T = Dialog | Path | Connector> {
	id: string;
	key: number;
	type: ConversationNodeType;
	data: T;
	coords: { x: number; y: number };
}

export enum ConversationNodeType {
	/** It is a special type of dialog, can be identified through isHead: true */
	Conversation,
	Dialog,
	Path,
	ExternalConversation,
	Connector
}

export interface INodeConfig {
	width: number;
	height: number;
}

export interface Conversation extends NodeData {
	id?: number;
	description: string;
	// all the below properties will be saved to and Populated from ConverationDetail JSON.
	imageUrl?: string | null;
	pageId: string | null;
	pageMetaData: ImageMetaData | null;
	contentId: string | null;
}
export interface Dialog extends NodeData {
	retryCount: number;
	text: string;
	isHead?: boolean;
	imageUrl: string | null;
	pageId: string | null;
	pageMetaData: ImageMetaData | null;
	contentId: string | null;
	voiceUrl: string | null;
	order: number;
	silentDialogText: DialogText[] | null;
	fallbackDialogText: DialogText[] | null;
	interactionStyleId: number;
	isConclusion: boolean;
}

export interface DialogText {
	id: string;
	key: number;
	dialogType: number | MasterConversationType;
	value: string;
	order: number;
	imageUrl: string | null;
	voiceUrl: string | null;
}
export interface Path extends NodeData {
	isFallback: boolean;
	imageUrl: string | null;
	pageId: string | null;
	pageMetaData: ImageMetaData | null;
	contentId: string | null;
	order: number;
	entityValueIds: string[];
	intents: PathIntent[];
	interactionStyleId: number;
}

export interface PathIntent {
	id: string;
}

export interface Connector {
	sourceId: string;
	targetId?: string;
	name?: string;
}

export interface NodeData {
	name: string;
}

export interface GroundConfig {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
	element: HTMLDivElement;
	width: number;
	height: number;
	minimapWidth: number;
}

export enum BuilderEventType {
	add = "BUILDER_ADD",
	edit = "BUILDER_EDIT",
	update = "BUILDER_UPDATE",
	delete = "BUILDER_DELETE",
	move = "BUILDER_MOVE"
}

export enum UtternceModalMode {
	Add,
	Edit
}

export interface FileUploads {
	id: string;
	nodeId: string | number; // number (for conversationId) would set in case of no node is selected
	fileName: string;
	size: number;
	uploaded: number;
}

export const ConversationNodeImagePath: { [key in ConversationNodeType]: string } = {
	[ConversationNodeType.Conversation]: "Conversation",
	[ConversationNodeType.Dialog]: "Dialog",
	[ConversationNodeType.Path]: "Path",
	[ConversationNodeType.Connector]: "Connector",
	[ConversationNodeType.ExternalConversation]: "External"
};

export type INodeUpdate<T> = { id: string; data: Partial<T> };
export type IConnectorAdd = { sourceId: string; targetId: string; connectorId: string };
export type INodes = { [key: string]: ConversationNode };

export interface IMetaData {
	changes: string[];
	nodes: INodes;
	intents: EntityDataSet<Intent>;
	entities: EntityDataSet<Entity>;
	conversationImageInfo?: {
		imageUrl: string;
		pageId: string;
		contentId: string;
		pageMetaData: ImageMetaData;
	};
}

export interface EntityDataSet<T = any> {
	ids: string[];
	directory: EntityDataSetDirectory<T>;
}

export type EntityDataSetDirectory<T> = { [key: string]: T };

export function getEntitySetDefaultValue<T = any>(data?: T[]): EntityDataSet<T> {
	const ids: string[] = [];
	const directory: EntityDataSetDirectory<T> = {};
	if (isArray(data)) {
		data.forEach((item: any) => {
			ids.push(item.id);
			directory[item.id] = item;
		});
	}
	return { ids, directory };
}

export interface INodeCount {
	dialogCount: number;
	pathCount: number;
}

export interface UtteranceValidationResult {
	isUnique: boolean;
	isValidEntity: boolean;
	invalidEntityName: string;
}
